import {
  Children,
  createContext,
  isValidElement,
  useContext,
  useId,
} from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import clsx from 'clsx'

import { Flex, Heading, Text } from '@buffer-mono/popcorn'

import styles from './SectionCard.module.css'

/** Shares the generated heading id so `aria-labelledby` points at the title. */
const SectionCardHeadingContext = createContext<string | null>(null)

export interface SectionCardProps {
  /** Extra class on the card container. */
  className?: string
  children: ReactNode
}

/**
 * Card wrapper for an Insights section. Compose the header from
 * `SectionCard.Title`, `SectionCard.Subtitle` and `SectionCard.Actions` inside
 * a `SectionCard.Header`, followed by the section content:
 *
 * ```tsx
 * <SectionCard>
 *   <SectionCard.Header>
 *     <SectionCard.Title>Summary</SectionCard.Title>
 *     <SectionCard.Actions>{actions}</SectionCard.Actions>
 *     <SectionCard.Subtitle>{dateRange}</SectionCard.Subtitle>
 *   </SectionCard.Header>
 *   {content}
 * </SectionCard>
 * ```
 */
export function SectionCard({
  className,
  children,
}: SectionCardProps): JSX.Element {
  const headingId = useId()

  return (
    <SectionCardHeadingContext.Provider value={headingId}>
      <section
        className={clsx(styles.container, className)}
        aria-labelledby={headingId}
      >
        {children}
      </section>
    </SectionCardHeadingContext.Provider>
  )
}

interface SectionCardHeaderProps {
  children: ReactNode
}

/**
 * Header row. Title, subtitle and actions are placed via named grid areas (see
 * the CSS), so children may be passed in any order — actions are extracted by
 * type; grid areas, not DOM order, drive placement.
 */
function SectionCardHeader({ children }: SectionCardHeaderProps): JSX.Element {
  const childArray = Children.toArray(children)
  const actions = childArray.find(
    (child) => isValidElement(child) && child.type === SectionCardActions,
  )
  const titleAndSubtitle = childArray.filter(
    (child) => !(isValidElement(child) && child.type === SectionCardActions),
  )

  return (
    <div className={styles.header}>
      {titleAndSubtitle}
      {actions}
    </div>
  )
}

interface SectionCardTitleProps {
  /** Optional leading icon, shown in a subtle rounded badge before the title. */
  icon?: ReactNode
  /** Heading level for this card within the surrounding page hierarchy. */
  as?: 'h2' | 'h3'
  children: ReactNode
}

function SectionCardTitle({
  icon,
  as = 'h2',
  children,
}: SectionCardTitleProps): JSX.Element {
  const contextHeadingId = useContext(SectionCardHeadingContext)
  const fallbackHeadingId = useId()
  const headingId = contextHeadingId ?? fallbackHeadingId

  return (
    <Flex align="center" gap="xs" className={styles.title}>
      {icon ? (
        <div className={styles.iconWrapper} aria-hidden="true">
          {icon}
        </div>
      ) : null}
      <Heading size="sm" as={as} id={headingId}>
        {children}
      </Heading>
    </Flex>
  )
}

interface SectionCardSubtitleProps {
  className?: string
  children?: ReactNode
}

function SectionCardSubtitle({
  className,
  children,
}: SectionCardSubtitleProps): JSX.Element | null {
  if (children == null) return null

  return (
    <div className={clsx(styles.subtitle, className)}>
      {typeof children === 'string' ? (
        <Text size="sm" color="subtle">
          {children}
        </Text>
      ) : (
        children
      )}
    </div>
  )
}

interface SectionCardActionsProps {
  children?: ReactNode
}

function SectionCardActions({
  children,
}: SectionCardActionsProps): JSX.Element | null {
  if (children == null) return null

  return <div className={styles.headerActions}>{children}</div>
}

SectionCard.Header = SectionCardHeader
SectionCard.Title = SectionCardTitle
SectionCard.Subtitle = SectionCardSubtitle
SectionCard.Actions = SectionCardActions
